import * as React from 'react';
import styled from 'styled-components';

import { Button } from 'components/bootstrap';
import { OverlayTrigger, Spinner, Icon, IfPermitted } from 'components/common';
import {
  useDetectionChainEventDefinitionSummary,
  useDetectionChainEvents,
} from 'security-app/hooks/useSecurityEventsAPI';
import { useModalContext } from 'common/contexts';
import { Row, Col, Badge } from 'common/components';
import type { DetectionChainRule } from 'security-app/hooks/api/securityEventsAPI.types';

const StyledIcon = styled(Icon)<{ $disabled?: boolean }>`
  color: ${({ theme, $disabled }) => ($disabled ? theme.colors.text.disabled : theme.colors.text.primary)};
`;

const Title = styled.span<{ $disabled?: boolean }>`
  color: ${({ theme, $disabled }) => ($disabled ? theme.colors.text.disabled : theme.colors.text.primary)};
`;

const Note = styled.span`
  margin-top: ${({ theme }) => theme.spacings.sm};
  color: ${({ theme }) => theme.colors.text.disabled};
`;

type Props = {
  eventId: string;
};

function WidgetTitle({ dcId }: { dcId: string }) {
  const { setModal, setEntity } = useModalContext();
  const { detectionChainEvents, loadingDetectionChainEvents } = useDetectionChainEvents(dcId);

  const openEvidenceModal = (e: React.BaseSyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const { event_ids } = detectionChainEvents;
    setEntity(event_ids);
    setModal('ADD-EVIDENCE');
  };

  return (
    <Row $align="center" $justify="space-between">
      <span>Detection Chain Rules</span>
      {loadingDetectionChainEvents ? (
        <Spinner text="Loading associated events" />
      ) : (
        <Row $align="center" $justify="flex-end" $width="auto" $gap="md">
          <small>
            Associated events count{' '}
            <Badge variant="info" size="sm">
              {detectionChainEvents.event_count}
            </Badge>
          </small>
          <IfPermitted permissions={['security_event:edit', '*']}>
            <Button bsSize="xs" bsStyle="primary" onClick={openEvidenceModal}>
              Add events to an investigation
            </Button>
          </IfPermitted>
        </Row>
      )}
    </Row>
  );
}

function DetectionChainDetails({ chainRules }: { chainRules: Array<DetectionChainRule> }) {
  return (
    <Col>
      {chainRules.map((rule: DetectionChainRule) => (
        <Row key={rule.id} $gap="sm" $align="center">
          <StyledIcon name={rule.seen ? 'task' : 'scan_delete'} size="sm" $disabled={!rule.seen} />
          <Title $disabled={!rule.seen}>{rule.title}</Title>
        </Row>
      ))}
      <Note>
        <i>*Note: Grayed out rules have not been detected in your data</i>
      </Note>
    </Col>
  );
}

function DetectionChain({ eventId }: Props) {
  const { detectionChain, loadingDetectionChain } = useDetectionChainEventDefinitionSummary(eventId);

  if (loadingDetectionChain) return <Spinner text="Loading Detection Chain details" />;

  return (
    <OverlayTrigger
      trigger="click"
      rootClose
      placement="right"
      overlay={<DetectionChainDetails chainRules={detectionChain.rules} />}
      title={<WidgetTitle dcId={detectionChain.id} />}
      width={800}>
      <Row $gap="xxs" $align="center">
        <Button bsStyle="link">
          <strong>{detectionChain.title}</strong>
        </Button>
        <Icon name="arrow_right" size="sm" />
      </Row>
    </OverlayTrigger>
  );
}

export default DetectionChain;
